// public/utils.js

export function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

export function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Extract binder ID from any URL format for consistent color hashing.
 * @param {string} url - Any binder URL format
 * @returns {string} - The binder ID (e.g., "abc123" from "/mfb.abc123" or "https://moxfield.com/binders/abc123")
 */
export function extractBinderId(url) {
  if (!url) return '';

  // Parse as URL first to get the pathname
  let pathname = url;
  try {
    const urlObj = new URL(url, 'https://example.com');
    pathname = urlObj.pathname;
  } catch {
    // Not a valid URL, use as-is
  }

  // Short URL format in path: /mfb.abc123 -> abc123
  const shortMatch = pathname.match(/\/[a-z]+\.(.+)$/);
  if (shortMatch) {
    return shortMatch[1];
  }

  // Source URL format: extract last path segment
  // e.g., /binders/abc123 -> abc123
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    return segments[segments.length - 1];
  }

  return url;
}

/**
 * Get consistent binder color from any URL format.
 * @param {string} url - Any binder URL format
 * @returns {string} - Hex color string (e.g., "#7a8b6e")
 */
export function getBinderColor(url) {
  const binderId = extractBinderId(url);
  const hue = simpleHash(binderId) % 360;
  const color = hslToHex(hue, 31, 50);
  return color;
}

export function hslToHex(h, s, l) {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function getTcgPlayerLanguage(langCode) {
  const langMap = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    ja: 'Japanese',
    ko: 'Korean',
    ru: 'Russian',
    zhs: 'Chinese (Simplified)',
    zht: 'Chinese (Traditional)',
  };
  return langMap[langCode.toLowerCase()] || null;
}

export function rgbToHex(rgb) {
  if (!rgb || typeof rgb !== 'string' || rgb.indexOf('rgb') === -1) return '#ffffff';
  let sep = rgb.indexOf(',') > -1 ? ',' : ' ';
  let rgbArray = rgb
    .substr(rgb.indexOf('(') + 1)
    .split(')')[0]
    .split(sep);
  if (rgbArray.length < 3 || rgbArray.some((c) => isNaN(+c))) return '#ffffff';
  const r = (+rgbArray[0]).toString(16).padStart(2, '0');
  const g = (+rgbArray[1]).toString(16).padStart(2, '0');
  const b = (+rgbArray[2]).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export function darkenColor(hexColor, factor) {
  if (!hexColor || !hexColor.startsWith('#') || hexColor.length !== 7) {
    return hexColor;
  }
  let r = parseInt(hexColor.slice(1, 3), 16);
  let g = parseInt(hexColor.slice(3, 5), 16);
  let b = parseInt(hexColor.slice(5, 7), 16);
  r = Math.max(0, Math.floor(r * factor));
  g = Math.max(0, Math.floor(g * factor));
  b = Math.max(0, Math.floor(b * factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function getCardDisplayName(cardData, defaultName = 'Unknown Card') {
  if (!cardData) return defaultName;
  if (
    Array.isArray(cardData.card_faces) &&
    cardData.card_faces.length > 0 &&
    cardData.card_faces[0].name
  ) {
    return cardData.card_faces[0].name;
  }
  return cardData.name || defaultName;
}

export function getCurrencySymbol(currencyCode) {
  switch (currencyCode?.toLowerCase()) {
    case 'eur':
      return '€';
    case 'usd':
    default:
      return '$';
  }
}

export function determineCurrencyFromLanguage() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone && timeZone.toLowerCase().startsWith('europe/')) return 'eur';
  } catch (error) {
    console.error('Error determining timezone:', error);
  }
  return 'usd';
}

export function waitForImageLoad(imgElement) {
  return new Promise((resolve) => {
    if (imgElement.complete && imgElement.naturalWidth > 0) {
      resolve();
    } else {
      const loadHandler = () => {
        cleanup();
        resolve();
      };
      const errorHandler = () => {
        cleanup();
        resolve(); // Resolve even on error to not block the UI
      };
      const cleanup = () => {
        imgElement.removeEventListener('load', loadHandler);
        imgElement.removeEventListener('error', errorHandler);
      };
      imgElement.addEventListener('load', loadHandler);
      imgElement.addEventListener('error', errorHandler);
    }
  });
}

/**
 * Wait for all card images on a page element to load with timeout fallback
 * @param {HTMLElement} pageElement - The .paper element containing card images
 * @param {number} timeoutMs - Maximum time to wait (default 3000ms)
 * @returns {Promise<{loaded: number, failed: number, timedOut: boolean}>}
 */
export function waitForPageImages(pageElement, timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (!pageElement) {
      resolve({ loaded: 0, failed: 0, timedOut: false });
      return;
    }

    const images = /** @type {NodeListOf<HTMLImageElement>} */ (
      pageElement.querySelectorAll('.card-grid img')
    );
    if (images.length === 0) {
      resolve({ loaded: 0, failed: 0, timedOut: false });
      return;
    }

    let loaded = 0;
    let failed = 0;
    let resolved = false;

    const checkComplete = () => {
      if (resolved) return;
      if (loaded + failed >= images.length) {
        resolved = true;
        clearTimeout(timeout);
        resolve({ loaded, failed, timedOut: false });
      }
    };

    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve({ loaded, failed, timedOut: true });
      }
    }, timeoutMs);

    images.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        loaded++;
        checkComplete();
      } else if (img.complete) {
        // complete but no naturalWidth = error
        failed++;
        checkComplete();
      } else {
        const onLoad = () => {
          loaded++;
          cleanup();
          checkComplete();
        };
        const onError = () => {
          failed++;
          cleanup();
          checkComplete();
        };
        const cleanup = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
        };
        img.addEventListener('load', onLoad);
        img.addEventListener('error', onError);
      }
    });

    // Check if all already complete
    checkComplete();
  });
}

export function generateRandomId(length = 16) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}
